import type { Scene } from './scene';
import { createVoxelCollision, MeshCollision } from './scene-collision-runtime';
import type { SceneCollision, VoxelMetadata } from './scene-collision-runtime';

type CollisionKind = 'voxel' | 'mesh';

type CollisionInputFile = {
    filename: string;
    url?: string;
    contents?: Blob;
};

type CollisionLoadBlobs = {
    kind: CollisionKind;
    name?: string;
    jsonBlob?: Blob;
    jsonName?: string;
    binBlob?: Blob;
    binName?: string;
    meshBlob?: Blob;
    meshName?: string;
    meshUrl?: string;
};

type CollisionExportFile = {
    path: string;
    blob: Blob;
};

type SceneCollisionExport = {
    kind: CollisionKind;
    name?: string;
    files: {
        json?: CollisionExportFile;
        bin?: CollisionExportFile;
        mesh?: CollisionExportFile;
    };
};

type SceneCollisionSummary = {
    hasCollision: boolean;
    kind: CollisionKind | null;
    name?: string;
    paths?: {
        json?: string;
        bin?: string;
        mesh?: string;
    };
};

const basename = (filename: string) => filename.split('?')[0].replace(/\\/g, '/').split('/').pop() ?? filename;
const lowerBasename = (filename: string) => basename(filename).toLowerCase();

const sanitizeBundleName = (name: string) => basename(name).replace(/[\\/:*?"<>|]/g, '_');

const fetchBlob = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
    }
    return response.blob();
};

const resolveSidecarUrl = (sidecarName: string, mainUrl?: string) => {
    if (!mainUrl) return undefined;
    return new URL(sidecarName, new URL(mainUrl, window.location.href)).href;
};

const fileBlob = async (file: CollisionInputFile) => {
    if (file.contents) return file.contents;
    if (file.url) return fetchBlob(file.url);
    throw new Error(`Collision file '${file.filename}' has no contents or URL`);
};

const blobToArrayBuffer = async (blob: Blob) => blob.arrayBuffer();

const blobText = async (blob: Blob) => new Response(blob).text();

const createSummary = (state: SceneCollisionExport | null): SceneCollisionSummary => {
    return {
        hasCollision: !!state,
        kind: state?.kind ?? null,
        name: state?.name,
        paths: state ? {
            json: state.files.json?.path,
            bin: state.files.bin?.path,
            mesh: state.files.mesh?.path
        } : undefined
    };
};

const registerSceneCollisionEvents = (scene: Scene) => {
    let collision: SceneCollision | null = null;
    let exportState: SceneCollisionExport | null = null;
    let objectUrl: string | null = null;

    const events = scene.events;

    const revokeObjectUrl = () => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
    };

    const setCollision = (nextCollision: SceneCollision, nextExport: SceneCollisionExport) => {
        collision = nextCollision;
        exportState = nextExport;
        scene.forceRender = true;
        events.fire('scene.collisionChanged', createSummary(exportState));
    };

    const clearCollision = () => {
        revokeObjectUrl();
        collision = null;
        exportState = null;
        scene.forceRender = true;
        events.fire('scene.collisionChanged', createSummary(null));
    };

    const loadVoxelBlobs = async (input: CollisionLoadBlobs) => {
        if (!input.jsonBlob || !input.binBlob) {
            throw new Error('Voxel collision requires both .voxel.json and .voxel.bin sidecars');
        }

        const metadata = JSON.parse(await blobText(input.jsonBlob)) as VoxelMetadata;
        const binary = await blobToArrayBuffer(input.binBlob);
        const nextCollision = createVoxelCollision(metadata, binary);
        const jsonPath = sanitizeBundleName(input.jsonName ?? input.name ?? 'collision.voxel.json');
        const binPath = sanitizeBundleName(input.binName ?? jsonPath.replace(/\.voxel\.json$/i, '.voxel.bin'));

        revokeObjectUrl();
        setCollision(nextCollision, {
            kind: 'voxel',
            name: input.name ?? jsonPath.replace(/\.voxel\.json$/i, ''),
            files: {
                json: { path: jsonPath, blob: input.jsonBlob },
                bin: { path: binPath, blob: input.binBlob }
            }
        });
    };

    const loadMeshBlobs = async (input: CollisionLoadBlobs) => {
        let meshUrl = input.meshUrl;
        let meshBlob = input.meshBlob ?? null;
        if (!meshUrl) {
            if (!meshBlob) {
                throw new Error('Mesh collision requires a .collision.glb file');
            }
            meshUrl = URL.createObjectURL(meshBlob);
            revokeObjectUrl();
            objectUrl = meshUrl;
        } else {
            revokeObjectUrl();
        }

        const nextCollision = await MeshCollision.fromGlb(scene.app, meshUrl);
        if (!meshBlob && input.meshUrl) {
            meshBlob = await fetchBlob(input.meshUrl);
        }

        const meshPath = sanitizeBundleName(input.meshName ?? input.name ?? 'collision.collision.glb');
        setCollision(nextCollision, {
            kind: 'mesh',
            name: input.name ?? meshPath.replace(/\.collision\.glb$/i, ''),
            files: {
                mesh: { path: meshPath, blob: meshBlob }
            }
        });
    };

    const loadCollisionBlobs = async (input: CollisionLoadBlobs) => {
        if (input.kind === 'voxel') {
            await loadVoxelBlobs(input);
        } else {
            await loadMeshBlobs(input);
        }
        return createSummary(exportState);
    };

    const loadCollisionFiles = async (files: CollisionInputFile[]) => {
        const byName = new Map<string, CollisionInputFile>();
        for (const file of files) {
            byName.set(lowerBasename(file.filename), file);
        }

        const voxelJson = files.find(file => lowerBasename(file.filename).endsWith('.voxel.json'));
        if (voxelJson) {
            const jsonName = basename(voxelJson.filename);
            const binName = jsonName.replace(/\.voxel\.json$/i, '.voxel.bin');
            const voxelBin = byName.get(binName.toLowerCase());
            const binBlob = voxelBin ? await fileBlob(voxelBin) : await fetchBlob(resolveSidecarUrl(binName, voxelJson.url) ?? binName);

            return loadCollisionBlobs({
                kind: 'voxel',
                name: jsonName.replace(/\.voxel\.json$/i, ''),
                jsonBlob: await fileBlob(voxelJson),
                jsonName,
                binBlob,
                binName
            });
        }

        const meshFile = files.find(file => lowerBasename(file.filename).endsWith('.collision.glb'));
        if (meshFile) {
            const meshName = basename(meshFile.filename);
            return loadCollisionBlobs({
                kind: 'mesh',
                name: meshName.replace(/\.collision\.glb$/i, ''),
                meshBlob: meshFile.contents ? await fileBlob(meshFile) : undefined,
                meshUrl: meshFile.contents ? undefined : meshFile.url,
                meshName
            });
        }

        throw new Error('No collision sidecar found. Expected .voxel.json/.voxel.bin or .collision.glb.');
    };

    events.function('scene.collision', () => createSummary(exportState));
    events.function('scene.collision.collider', () => collision);
    events.function('scene.collision.export', () => exportState);
    events.function('scene.collision.loadFiles', loadCollisionFiles);
    events.function('scene.collision.loadBlobs', loadCollisionBlobs);

    events.on('scene.collision.clear', clearCollision);
};

export {
    registerSceneCollisionEvents
};

export type {
    CollisionKind,
    CollisionInputFile,
    CollisionLoadBlobs,
    SceneCollisionExport,
    SceneCollisionSummary
};
